import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { orderUpdatedPayloadSchema } from '../data/order-ws';
import { ordersQueryOptions } from '../query/query-options';
import { useConnectionStatus } from '../websocket/use-connection-status';
import { useStoreWebSocket } from '../websocket/use-store-websocket';

/**
 * Keep the store's order LIST current from the WebSocket (ORD-460). Sibling to
 * useOrderEventListener, which does the same job for one open order.
 *
 * Deliberately much simpler than that one. The edit page needs a version
 * baseline and an in-flight-write park because it races its own PATCHes against
 * their own echoes; the list issues no writes at all, so every push is someone
 * else's news and the right response is always "refetch". The card renders
 * aggregates — status, totals, updated_at — so there is nothing finer to
 * reconcile than the list itself.
 *
 * Not filtered by order id, on purpose: a push for an order the list has never
 * heard of is exactly the create it most needs to hear about.
 *
 * Mounted on the store layout rather than the list page, so the cache stays
 * right everywhere it is read — useDraftOrders backs the one-draft-per-vendor
 * guard in useMaybeCreateOrder, which is only as good as the list behind it.
 */
export function useOrdersListEventListener(storeId: string): void {
  const { subscribe } = useStoreWebSocket();
  const queryClient = useQueryClient();

  // `exact` is load-bearing: ['stores', id, 'orders'] is a PREFIX of every
  // per-order key ['stores', id, 'orders', orderId], so a default invalidate
  // here would also mark the open order's query stale on every push and
  // refetch it behind the edit page's own listener.
  const invalidateList = useCallback((): void => {
    void queryClient.invalidateQueries({
      queryKey: ordersQueryOptions(storeId).queryKey,
      exact: true,
    });
  }, [queryClient, storeId]);

  useEffect(
    () => subscribe('order_updated', orderUpdatedPayloadSchema, invalidateList),
    [subscribe, invalidateList]
  );

  // wsHello → webPostConnect replays store_updated only, so any order_updated
  // emitted while the socket was down is simply gone. A reconnect therefore
  // owes exactly one refetch. The mount's connecting → open is not a
  // reconnect — the query's own mount fetch covers that.
  const connection = useConnectionStatus();
  const previousConnectionRef = useRef(connection);
  useEffect(() => {
    const previous = previousConnectionRef.current;
    previousConnectionRef.current = connection;
    if (previous === 'closed' && connection === 'open') {
      invalidateList();
    }
  }, [connection, invalidateList]);
}
